import { EndpointCredential } from './ConnectionCredentials';
import { SetupDialog } from './ConnectionSetupForm';
export { SetupDialog } from './ConnectionSetupForm';
import { liveState, useReadinessClock } from './connection-state';
import { IncomingConnections } from './IncomingConnections';
import {
  ConnectionsIntroduction,
  type ConnectionStarter,
} from './ConnectionsIntroduction';
import { ConnectionPlans } from './ConnectionPlanCard';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import {
  connectionApi,
  statusLabel,
  type Connection,
} from '../../lib/connections-api';
import { useAuth } from '../../lib/auth';
import { usePageHelpDetails } from '../../lib/page-context';
import { ConnectionOperationCard } from './ConnectionOperationCard';
import { Icon } from '../ui/Icon';
import {
  ConnectionAbilities, abilitySelections, endpointName, endpointReady,
  selectedAbilities, selectionPayload, type AbilitySelection,
} from './ConnectionAbilities';
import styles from './Connections.module.css';

export function ConnectionsPage(props: { connectionId?: string }) {
  const { bootstrap } = useAuth();
  return (
    <ConnectionsWorkspace
      key={`${bootstrap?.workspace.id}:${bootstrap?.user?.id}`}
      {...props}
    />
  );
}
function ConnectionsWorkspace({ connectionId }: { connectionId?: string }) {
  const { bootstrap } = useAuth();
  const scope = `${bootstrap?.workspace.id}:${bootstrap?.user?.id}`;
  const [direction, setDirection] = useState<'outgoing' | 'incoming'>(
    'outgoing',
  );
  const list = useQuery({
    queryKey: ['connections', scope],
    queryFn: () => connectionApi.list(),
    enabled: !connectionId && direction === 'outgoing',
    refetchInterval: 30000,
  });
  const detail = useQuery({
    queryKey: ['connection', scope, connectionId],
    queryFn: () => connectionApi.detail(connectionId!),
    enabled: !!connectionId,
    refetchInterval: 30000,
  });
  const [adding, setAdding] = useState(false),
    [more, setMore] = useState<Connection[]>([]),
    [cursor, setCursor] = useState<string | null | undefined>();
  const [pageError, setPageError] = useState('');
  const [pageNotice, setPageNotice] = useState('');
  const [starter, setStarter] = useState<ConnectionStarter>('custom');
  usePageHelpDetails({
    direction: connectionId ? 'outgoing' : direction,
    view: connectionId ? 'connection-detail' : adding && direction === 'outgoing' ? 'setup' : 'connection-list',
    ...(adding && direction === 'outgoing' ? { starter } : {}),
    ...(detail.data ? {
      connection: {
        id: detail.data.id,
        name: detail.data.name,
        state: liveState(detail.data),
        enabled: detail.data.enabled,
        endpoints: detail.data.endpoints.map(({ protocol, status }) => ({ protocol, status })),
        capabilities: detail.data.capabilities.map(({ id, title, kind, selected, policy, available }) =>
          ({ id, title, kind, selected, policy, available })),
      },
    } : {}),
  });
  const start = (value: ConnectionStarter) => {
    setStarter(value);
    setAdding(true);
  };
  const navigate = useNavigate();
  useReadinessClock();
  const next = async () => {
    try {
      const page = await connectionApi.list(
        cursor ?? list.data?.nextCursor ?? undefined,
      );
      setMore((v) => [...v, ...page.items]);
      setCursor(page.nextCursor);
      setPageError('');
    } catch (e) {
      setPageError(
        e instanceof Error ? e.message : 'Could not load more connections.',
      );
    }
  };
  return (
    <div className={styles.page} key={scope}>
      <header className={styles.heading}>
        <div>
        {connectionId && <Link className={styles.backLink} to="/app/connections"><Icon name="chevron-left" size={15} />External connections</Link>}
        <h1>
          {connectionId ? (detail.data?.name ?? 'Connection') : 'External connections'}
        </h1>
        </div>
      </header>
      {!connectionId && (
        <nav className={styles.directionTabs} aria-label="Connection direction">
          <button
            type="button"
            aria-pressed={direction === 'outgoing'}
            onClick={() => setDirection('outgoing')}
          >
            <strong>Outgoing</strong>
            <span>Echo uses your other systems</span>
          </button>
          <button
            type="button"
            aria-pressed={direction === 'incoming'}
            onClick={() => setDirection('incoming')}
          >
            <strong>Incoming</strong>
            <span>Other systems use Resonance</span>
          </button>
        </nav>
      )}
      {!connectionId && direction === 'incoming' ? (
        <IncomingConnections />
      ) : (
        <>
          {!connectionId && <ConnectionsIntroduction onStart={start} />}
          {adding && (
            <SetupDialog
              key={starter}
              starter={starter}
              onClose={() => setAdding(false)}
              onSaved={(c) => {
                setAdding(false);
                void navigate({
                  to: '/app/connections/$connectionId',
                  params: { connectionId: c.id },
                });
              }}
            />
          )}
          {(list.isError || detail.isError) && (
            <p role="alert">
              {(list.error ?? detail.error)?.message}{' '}
              <button
                onClick={() =>
                  void (connectionId ? detail.refetch() : list.refetch())
                }
              >
                Retry
              </button>
            </p>
          )}
          {(connectionId ? detail.isLoading : list.isLoading) && (
            <p>Loading connections…</p>
          )}
          {!connectionId && (
            <div className={styles.savedHeading}>
              <h2>Your connections</h2>
              <p className={styles.muted}>
                {list.data?.items.length === 0
                  ? 'No systems connected yet. Choose a starting point above to add your first one.'
                  : 'Manage permissions, guidance, and recent use for each system.'}
              </p>
            </div>
          )}
          {!connectionId && pageNotice && <p role="status" className={styles.feedback}>{pageNotice}</p>}
          {!connectionId &&
            [...(list.data?.items ?? []), ...more].map((c) => (
              <Link
                key={c.id}
                className={styles.card}
                to="/app/connections/$connectionId"
                params={{ connectionId: c.id }}
              >
                <div className={styles.row}>
                  <strong>{c.name}</strong>
                  <span>
                    <i
                      className={styles.dot}
                      data-ready={liveState(c) === 'ready'}
                    />
                    {statusLabel(liveState(c))}
                  </span>
                </div>
                <span className={styles.muted}>
                  {
                    c.capabilities.filter(
                      (cap) => cap.selected && cap.available,
                    ).length
                  }{' '}
                  selected abilities
                </span>
              </Link>
            ))}
          {!connectionId &&
            (cursor === undefined ? list.data?.nextCursor : cursor) && (
              <button onClick={() => void next()}>Load more</button>
            )}
          {pageError && <p role="alert">{pageError}</p>}
          {connectionId && detail.data && (
            <ConnectionEditor
              key={`${scope}:${connectionId}`}
              connection={detail.data}
              onDeleted={() => {
                setMore((items) => items.filter((item) => item.id !== connectionId));
                setPageNotice('Connection deleted.');
                void navigate({ to: '/app/connections', replace: true });
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
export function ConnectionEditor({ connection: c, onDeleted }: { connection: Connection; onDeleted: () => void }) {
  useReadinessClock();
  const { bootstrap } = useAuth();
  const scope = `${bootstrap?.workspace.id}:${bootstrap?.user?.id}`;
  const cache = useQueryClient();
  const [name, setName] = useState(c.name),
    [instructions, setInstructions] = useState(c.instructions),
    [baseRevision, setBaseRevision] = useState(c.revision);
  const [selections, setSelections] = useState(() => abilitySelections(c));
  const draftKey = (value: { name: string; instructions: string; selections: AbilitySelection[] }) =>
    JSON.stringify({ ...value, selections: [...value.selections].sort((a, b) => a.id.localeCompare(b.id)) });
  const baseline = useRef(draftKey({ name: c.name, instructions: c.instructions, selections: abilitySelections(c) }));
  const dirty = baseline.current !== draftKey({ name, instructions, selections });
  const [adding, setAdding] = useState(false),
    [deleting, setDeleting] = useState(false),
    [notice, setNotice] = useState('');
  const permissions = useRef<HTMLElement>(null);
  const [selectionAttempted, setSelectionAttempted] = useState(false);
  const [testCapability, setTestCapability] = useState(''),
    [args, setArgs] = useState('{}');
  const operations = useQuery({
    queryKey: ['connection-operations', scope, c.id],
    queryFn: () => connectionApi.operations(c.id),
    refetchInterval: 10000,
  });
  const reset = (value: Connection = c) => {
    setName(value.name);
    setInstructions(value.instructions);
    setBaseRevision(value.revision);
    const next = abilitySelections(value);
    setSelections(next);
    baseline.current = draftKey({ name: value.name, instructions: value.instructions, selections: next });
  };
  // Quiet rechecks must not create false revision conflicts. Real edits remain
  // attached to their original revision until the owner explicitly reconciles.
  useEffect(() => {
    if (!dirty) reset(c);
  }, [c.revision]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = (data: Connection) => {
    if (!dirty) reset(data);
    cache.setQueryData(['connection', scope, c.id], data);
    void cache.invalidateQueries({ queryKey: ['connections'] });
    void cache.invalidateQueries({ queryKey: ['navigation-status'] });
  };
  const mutation = useMutation({
    mutationFn: async ({ action, body }: { action: string; body?: unknown; enable?: boolean }) => {
      setNotice('');
      if (action === 'check') return connectionApi.check(c.id);
      return connectionApi.patch(c.id, body);
    },
    onSuccess: async (data, vars) => {
      if (vars.action === 'save') reset(data);
      if (vars.action === 'pause') setBaseRevision(data.revision);
      refresh(data);
      if (vars.enable) {
        // A successful PATCH means access was saved, not that the provider is
        // reachable. Publish that revision even if the follow-up check fails.
        try {
          const checked = await connectionApi.check(c.id);
          refresh(checked);
          setNotice(liveState(checked) === 'ready'
            ? ''
            : 'Access is saved. The connection needs attention before Echo can use it.');
        } catch {
          setNotice('Access is saved, but verification failed. Check the connection again.');
        }
      } else {
        setNotice(vars.action === 'check'
          ? data.endpoints.some(endpointReady) ? 'Connection check passed.' : 'Connection check finished. See the endpoint status in settings.'
          : vars.action === 'pause' ? 'Access is off. Your permissions are saved.' : 'Changes saved.');
      }
    },
  });
  const prepare = useMutation({
    mutationFn: () => connectionApi.prepare(c.id, {
      capabilityId: testCapability, arguments: JSON.parse(args), requestKey: crypto.randomUUID(),
    }),
    onSuccess: () => void cache.invalidateQueries({ queryKey: ['connection-operations'] }),
  });
  const disabled = mutation.isPending || !!c.disconnected_at;
  const selected = selectedAbilities(c, selections).length;
  const needsSelection = selectionAttempted && selected === 0;
  const savedSelected = c.capabilities.filter((cap) => cap.selected && cap.available).length;
  const state = liveState(c);
  const reachable = c.endpoints.some(endpointReady);
  const save = (enable = false) => mutation.mutate({
    action: 'save', enable,
    body: {
      expectedRevision: baseRevision, name, instructions,
      selections: selectionPayload(c, selections),
      ...(enable ? { enabled: true } : {}),
    },
  });
  const enable = () => {
    if (selected) save(true);
    else {
      setSelectionAttempted(true);
      permissions.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      (permissions.current?.querySelector<HTMLElement>('input[type="checkbox"]:not(:disabled)')
        ?? permissions.current)?.focus({ preventScroll: true });
    }
  };
  const stateTitle = c.disconnected_at ? 'Disconnected'
    : !c.enabled ? reachable ? 'Connected · Access off' : 'Access off'
    : state === 'ready' ? 'On · Ready for Echo'
    : state === 'needs_setup' ? 'Choose abilities to finish setup' : `On · ${statusLabel(state)}`;
  const stateDescription = c.disconnected_at ? 'Create a new connection to use this system again.'
    : !c.enabled ? savedSelected
      ? 'Your connection and permissions are saved. Turn on access when you’re ready.'
      : 'Your endpoint is saved. Select the abilities Echo can use, then turn on access.'
    : state === 'ready' ? 'Echo can use your selected abilities in chat with the permissions below.'
    : 'Access is enabled. Echo can use this connection once its selected abilities pass a fresh check.';
  return <div className={styles.detailPage}>
    <section className={`${styles.card} ${styles.connectionOverview}`}>
      <div className={styles.overviewMain}>
        <span className={styles.connectionSymbol}><Icon name="link" size={24} /></span>
        <div><span className={styles.statusBadge} data-ready={state === 'ready'}>
          <i className={styles.dot} data-ready={state === 'ready'} />{stateTitle}
        </span><p>{stateDescription}</p></div>
      </div>
      {!c.disconnected_at && <button type="button" className={!c.enabled ? styles.primaryButton : undefined}
        disabled={disabled || (!c.enabled && baseRevision !== c.revision)}
        onClick={() => c.enabled ? mutation.mutate({ action: 'pause', body: { expectedRevision: c.revision, enabled: false } }) : enable()}>
        {mutation.isPending ? 'Saving…' : c.enabled ? 'Turn off access' : dirty && selected ? 'Save and turn on' : 'Turn on connection'}
      </button>}
      <div className={styles.overviewMeta}>
        <span><strong>{c.capabilities.filter((cap) => cap.available).length}</strong> abilities found</span>
        <span><strong>{savedSelected}</strong> selected</span>
        {c.endpoints.map((endpoint) => <span key={endpoint.id}>{endpoint.protocol.toUpperCase()} · {endpointName(endpoint)}</span>)}
      </div>
    </section>
    {notice && <p role="status" className={styles.feedback}><Icon name="alert-circle" size={17} />{notice}</p>}
    {mutation.isError && <p role="alert" className={styles.error}>{mutation.error.message}</p>}
    <div className={styles.detailColumns}>
      <div className={styles.detailMain}>
        <section className={`${styles.card} ${styles.permissionsSection}`} ref={permissions}
          tabIndex={-1} data-selection-required={needsSelection}>
          <div className={styles.sectionHeading}><div><h2>What Echo can use</h2><p>Select abilities and choose when Echo should ask you first.</p></div></div>
          <ConnectionAbilities connection={c} selections={selections} onChange={setSelections} disabled={disabled}
            selectionRequired={needsSelection} />
          <p className={styles.sectionFootnote}>New abilities stay off until you select them. Agent tasks and tools that require review always ask first.</p>
        </section>
        <section className={styles.card}>
          <div className={styles.sectionHeading}><div><h2>Guidance for Echo</h2><p>Tell Echo when this system is useful and what context to look for.</p></div></div>
          <label>Connection name<input maxLength={200} value={name} disabled={disabled} onChange={(event) => setName(event.target.value)} /></label>
          <label>Instructions<textarea maxLength={8000} value={instructions} disabled={disabled} onChange={(event) => setInstructions(event.target.value)}
            placeholder="Use this connection when…" /></label>
          <p className={styles.sectionFootnote}>{instructions.length.toLocaleString()}/8,000 · Guidance doesn’t change permissions.</p>
        </section>
        <div className={`${styles.card} ${styles.saveBar}`}>
          <span>{dirty ? 'Unsaved changes' : 'Your permissions are saved'}{!c.enabled && selected ? ' · Access is off' : ''}</span>
          <div className={styles.actions}>
            {dirty && <button disabled={disabled} onClick={() => reset()}>Cancel edits</button>}
            {dirty && !c.enabled && selected > 0 && <button disabled={disabled || baseRevision !== c.revision || !name.trim()} onClick={() => save()}>Save with access off</button>}
            <button className={styles.primaryButton}
              disabled={disabled || baseRevision !== c.revision || !name.trim() || (!dirty && (c.enabled || !selected))}
              onClick={() => save(!c.enabled && selected > 0)}>
              {mutation.isPending ? 'Saving…' : !c.enabled && selected > 0 ? 'Save and turn on' : 'Save changes'}
            </button>
          </div>
          {baseRevision !== c.revision && <p role="status" className={styles.revisionNotice}>
            This connection changed while you were editing. Your edits are preserved.
            <button disabled={disabled} onClick={() => { setBaseRevision(c.revision); setNotice('Latest revision loaded. Review your selections before saving.'); }}>Use latest revision and keep my edits</button>
          </p>}
        </div>
        <section className={styles.card}>
          <div className={styles.sectionHeading}><div><h2>Recent activity</h2><p>Requests, reviews, and results from this connection.</p></div></div>
          <ConnectionPlans connectionId={c.id} />
          {operations.isLoading && <p className={styles.muted}>Loading recent activity…</p>}
          {operations.isError && <p role="alert">Could not load recent activity.</p>}
          {operations.data?.items.length === 0 && <div className={styles.activityEmpty}><Icon name="message-circle" size={22} /><strong>No activity yet</strong><p>{!c.enabled ? 'Turn on this connection, then ask Echo to use it in chat.' : 'Ask Echo to use this connection in chat. Requests and results will appear here.'}</p></div>}
          {operations.data?.items.map((operation) => <ConnectionOperationCard key={operation.id} operationId={operation.id} />)}
        </section>
      </div>
      <aside className={styles.detailAside}>
        <section className={styles.card}>
          <div className={styles.sectionHeading}><div><h2>Connection settings</h2><p>Endpoint health and authentication.</p></div></div>
          {c.endpoints.map((endpoint) => <div className={styles.endpointCard} key={endpoint.id}>
            <div className={styles.endpointHeading}><strong>{endpointName(endpoint)}</strong><span>{endpoint.protocol.toUpperCase()}</span></div>
            <p className={styles.endpointUrl}>{endpoint.url}</p>
            <span className={styles.endpointHealth}><i className={styles.dot} data-ready={endpointReady(endpoint)} />{endpointReady(endpoint) ? 'Endpoint verified' : endpoint.status === 'ready' ? 'Awaiting fresh check' : statusLabel(endpoint.status)}</span>
            {endpoint.safe_error && <p role="alert" className={styles.error}>{endpoint.safe_error}</p>}
            <EndpointCredential connection={c} endpoint={endpoint} onSaved={refresh} />
          </div>)}
          <div className={styles.settingsActions}><button disabled={disabled} onClick={() => mutation.mutate({ action: 'check' })}>{mutation.isPending ? 'Checking…' : 'Check connection'}</button>
            <button disabled={disabled} onClick={() => setAdding(true)}>Add endpoint or reconnect</button></div>
        </section>
        <section className={`${styles.card} ${styles.advancedSection}`}>
          <details><summary>Advanced</summary>
            <p className={styles.muted}>Test a selected ability with your own inputs. This creates a real request using its saved permissions.</p>
            <label>Ability<select value={testCapability} onChange={(event) => setTestCapability(event.target.value)}><option value="">Select an ability</option>{c.capabilities.filter((cap) => cap.selected && cap.available).map((cap) => <option key={cap.id} value={cap.id}>{cap.title}</option>)}</select></label>
            <label>Inputs (JSON)<textarea value={args} onChange={(event) => setArgs(event.target.value)} /></label>
            <button disabled={!testCapability || prepare.isPending || !c.enabled || disabled} onClick={() => prepare.mutate()}>Prepare request</button>
            {prepare.isError && <p role="alert">{prepare.error.message}</p>}
          </details>
        </section>
        {!c.disconnected_at && <button type="button" className={styles.deleteConnection} disabled={mutation.isPending}
          onClick={() => setDeleting(true)}>Delete connection</button>}
      </aside>
    </div>
    {adding && <SetupDialog connection={c} onClose={() => setAdding(false)} onSaved={(data) => { refresh(data); setAdding(false); }} />}
    {deleting && <DeleteConnectionDialog connection={c} onClose={() => setDeleting(false)} onDeleted={onDeleted} />}
  </div>;
}

function DeleteConnectionDialog({ connection, onClose, onDeleted }: {
  connection: Connection; onClose: () => void; onDeleted: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const title = useId();
  const cache = useQueryClient();
  const remove = useMutation({
    mutationFn: () => connectionApi.remove(connection.id, connection.revision),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['connections'] });
      void cache.invalidateQueries({ queryKey: ['navigation-status'] });
      onDeleted();
    },
  });
  useEffect(() => {
    const element = dialog.current!;
    const previous = document.activeElement as HTMLElement | null;
    element.showModal();
    cancel.current?.focus();
    return () => {
      if (element.open) element.close();
      previous?.focus({ preventScroll: true });
    };
  }, []);
  return <dialog ref={dialog} className={`${styles.setupDialog} ${styles.deleteDialog}`} aria-labelledby={title}
    onCancel={(event) => { event.preventDefault(); if (!remove.isPending) onClose(); }}>
    <div className={styles.card}>
      <h2 id={title}>Delete {connection.name}?</h2>
      <p>This removes the connection from your saved list and stops Echo from starting new work with it. You’ll need to connect it again to use it later.</p>
      <p className={styles.muted}>Requests already submitted may still finish. Their history is kept. Deleting here does not revoke the credential on the remote service.</p>
      {remove.isError && <p role="alert" className={styles.error}>{remove.error.message}</p>}
      <div className={styles.modalActions}>
        <button type="button" ref={cancel} disabled={remove.isPending} onClick={onClose}>Cancel</button>
        <button type="button" className={styles.destructiveButton} disabled={remove.isPending}
          onClick={() => remove.mutate()}>{remove.isPending ? 'Deleting…' : 'Delete connection'}</button>
      </div>
    </div>
  </dialog>;
}

